/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { secondsToMilliseconds } from '@app/domain/time';
import { isTruthy } from '@common/utils';
import {
  ActionName,
  ClusterActionCompletedResponse,
  ClusterActionResponse,
  ClusterActionResponseError,
  ResponseStatus,
} from '@shared/types/cluster-actions';
import { Observable, filter, map, throwError, timeout } from 'rxjs';

// https://developer.mozilla.org/en-US/docs/Web/API/setTimeout#maximum_delay_value
export const MAX_TIMEOUT = 2147483647;

export const completedClusterAction =
  (options?: { timeoutInMs: number }) =>
  <T extends ActionName>(source$: Observable<ClusterActionResponse<T>>) =>
    source$.pipe(
      filter((res): res is ClusterActionCompletedResponse<T> => res.status === ResponseStatus.COMPLETED),
      timeout({
        first: options?.timeoutInMs ?? secondsToMilliseconds(60),
        with: () =>
          throwError((): ClusterActionResponseError => ({ code: -1, message: 'Request timed out', stackTrace: [] })),
      }),
    );

type JobResponse<T extends ActionName> = { job: ClusterActionResponse<T>; jobCount: number };

const toDistributedJobResponse = <T extends ActionName>(res: unknown): JobResponse<T> | null => {
  if ('job' in (res as any) && 'jobCount' in (res as any)) {
    return res as JobResponse<T>;
  } else {
    return null;
  }
};

export const waitForAllDistributedJobsToFinish = <T extends ActionName>(source$: Observable<unknown>) => {
  let seenJobCount = 0;
  return source$.pipe(
    map((value) => toDistributedJobResponse<T>(value)),
    filter(isTruthy),
    // Basically, a takeWhile, but also passes errors along
    (source) => {
      return new Observable<JobResponse<T>>((subscriber) => {
        return source.subscribe({
          next(res) {
            if (res.job.status !== ResponseStatus.RUNNING) seenJobCount += 1;
            if (seenJobCount <= res.jobCount) {
              subscriber.next(res);
            } else {
              subscriber.complete();
            }
          },
          complete() {
            subscriber.complete();
          },
          error(err) {
            subscriber.error(err);
          },
        });
      });
    },
  );
};
